import React, { useState, useEffect, useCallback } from 'react';
import daoService from '../../services/daoService';
import {
  Box,
  Button,
  ButtonGroup,
  Center,
  CardBody,
  CardHeader,
  Container,
  Divider,
  Flex,
  Grid,
  GridItem,
  HStack,
  Heading,
  Icon,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  SimpleGrid,
  Spacer,
  Stat,
  StatArrow,
  StatHelpText,
  StatLabel,
  StatNumber,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  VStack,
  Badge,
  useToast,
  useDisclosure,
  Avatar,
  AvatarGroup,
  Wrap,
  WrapItem,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  SliderMark,
  Radio,
  RadioGroup,
  Stack,
  Spinner,
} from "@chakra-ui/react";
import { 
  CheckIcon, 
  CloseIcon, 
  EditIcon,
  InfoIcon,
  TriangleDownIcon,
  TriangleUpIcon,
  AddIcon,
  WarningIcon,
} from "@chakra-ui/icons";
import { FiTrendingUp, FiTrendingDown } from 'react-icons/fi';
import { WalletMultiButton } from "@solana/wallet-adapter-react-ui";
import { useWallet, useConnection } from "@solana/wallet-adapter-react";
import { 
  GiPirateCaptain,
  GiCompass,
  GiTreasureMap,
  GiShipWheel,
  GiAnchor,
  GiSpyglass,
  GiCutlass,
  GiMoneyStack,
  GiPirateHat,
  GiPirateSkull,
  GiChest,
  GiPirateFlag,
  GiCoins
} from 'react-icons/gi';
import AngelsKeyGate from '../../components/AngelsKeyGate';
import { PirateButton } from "../../components/PirateComponents";
import { PirateCard } from "../../components/PirateCard";
import { PirateTab } from "../../components/PirateTab";
import GoldenKeyNFTCard from "../../components/GoldenKeyNFTCard";
import StatsOverview from '../../components/StatsOverview';

// Theme constants
const goldColor = "#FFD700";
const grayTextColor = "gray.400";
const borderColor = "rgba(255, 215, 0, 0.3)";

// Mock data
const mockProposals = [
  {
    id: 1,
    title: "Increase Staking Rewards",
    description: "Proposal to increase the staking rewards by 20% for all Angels Key holders.",
    creator: "0x1234...5678",
    endDate: "2025-04-15",
    status: "active",
    category: "Treasury",
    activeVoters: 450,
    options: [
      { id: 1, text: "Approve", percentage: 75, votes: 338 },
      { id: 2, text: "Reject", percentage: 25, votes: 112 }
    ]
  },
  {
    id: 2,
    title: "New Game Mode Development",
    description: "Fund the development of a new PvP battle arena mode with special rewards.",
    creator: "0x9876...5432",
    endDate: "2025-04-20",
    status: "active",
    category: "Development",
    activeVoters: 380,
    options: [
      { id: 1, text: "Approve", percentage: 82, votes: 312 },
      { id: 2, text: "Reject", percentage: 18, votes: 68 }
    ]
  },
  {
    id: 3,
    title: "Community Events Program",
    description: "Launch monthly community events with exclusive NFT rewards for participants.",
    creator: "0x4567...8901",
    endDate: "2025-04-18",
    status: "active",
    category: "Community",
    activeVoters: 290,
    options: [
      { id: 1, text: "Approve", percentage: 65, votes: 189 },
      { id: 2, text: "Reject", percentage: 35, votes: 101 }
    ]
  },
  {
    id: 4,
    title: "Strategic Partnership with GameFi",
    description: "Establish partnership with leading GameFi platform for cross-promotion.",
    creator: "0x7890...1234",
    endDate: "2025-04-25",
    status: "active",
    category: "Partnership",
    activeVoters: 420,
    options: [
      { id: 1, text: "Approve", percentage: 70, votes: 294 },
      { id: 2, text: "Reject", percentage: 30, votes: 126 }
    ]
  },
  {
    id: 5,
    title: "Marketplace Fee Reduction",
    description: "Reduce marketplace trading fees from 2.5% to 1.5% to boost volume.",
    creator: "0x2345...6789",
    endDate: "2025-04-22",
    status: "active",
    category: "Economics",
    activeVoters: 520,
    options: [
      { id: 1, text: "Approve", percentage: 88, votes: 458 },
      { id: 2, text: "Reject", percentage: 12, votes: 62 }
    ]
  },
  {
    id: 6,
    title: "NFT Staking Program Update",
    description: "Implement tiered staking rewards based on NFT rarity and holding period.",
    creator: "0x3456...7890",
    endDate: "2025-04-28",
    status: "active",
    category: "Staking",
    activeVoters: 480,
    options: [
      { id: 1, text: "Approve", percentage: 92, votes: 442 },
      { id: 2, text: "Reject", percentage: 8, votes: 38 }
    ]
  },
  {
    id: 7,
    title: "Cross-Chain Bridge Implementation",
    description: "Deploy a cross-chain bridge to enable multi-chain NFT trading and rewards.",
    creator: "0x8901...2345",
    endDate: "2025-04-30",
    status: "active",
    category: "Technology",
    activeVoters: 550,
    options: [
      { id: 1, text: "Approve", percentage: 78, votes: 429 },
      { id: 2, text: "Reject", percentage: 22, votes: 121 }
    ]
  }
];

const completedProposals = [
  {
    id: 8,
    title: "Initial DAO Treasury Allocation",
    description: "Set up initial treasury pools and allocation percentages.",
    creator: "0x5678...9012",
    endDate: "2025-03-15",
    status: "completed",
    category: "Treasury",
    activeVoters: 680,
    result: "Approved",
    options: [
      { id: 1, text: "Approve", percentage: 95, votes: 646 },
      { id: 2, text: "Reject", percentage: 5, votes: 34 }
    ]
  },
  {
    id: 9,
    title: "Genesis NFT Holder Benefits",
    description: "Establish exclusive benefits for Genesis NFT holders.",
    creator: "0x6789...0123",
    endDate: "2025-03-20",
    status: "completed",
    category: "Community",
    activeVoters: 420,
    result: "Approved",
    options: [
      { id: 1, text: "Approve", percentage: 88, votes: 370 },
      { id: 2, text: "Reject", percentage: 12, votes: 50 }
    ]
  },
  {
    id: 10,
    title: "Game Development Roadmap",
    description: "Approve Q2-Q3 2025 game development milestones and budget.",
    creator: "0x7890...1234",
    endDate: "2025-03-25",
    status: "completed",
    category: "Development",
    activeVoters: 510,
    result: "Approved",
    options: [
      { id: 1, text: "Approve", percentage: 85, votes: 434 },
      { id: 2, text: "Reject", percentage: 15, votes: 76 }
    ]
  },
  {
    id: 11,
    title: "Marketing Campaign Budget",
    description: "Allocate funds for Q2 2025 marketing initiatives.",
    creator: "0x8901...2345",
    endDate: "2025-03-28",
    status: "completed",
    category: "Marketing",
    activeVoters: 490,
    result: "Rejected",
    options: [
      { id: 1, text: "Approve", percentage: 45, votes: 221 },
      { id: 2, text: "Reject", percentage: 55, votes: 269 }
    ]
  },
  {
    id: 12,
    title: "Community Moderator Elections",
    description: "Select community moderators for Discord and social channels.",
    creator: "0x9012...3456",
    endDate: "2025-03-30",
    status: "completed",
    category: "Governance",
    activeVoters: 580,
    result: "Approved",
    options: [
      { id: 1, text: "Approve", percentage: 92, votes: 534 },
      { id: 2, text: "Reject", percentage: 8, votes: 46 }
    ]
  }
];

const POOL_LIMITS = {
  'Rewards Pool': 50,
  'Reserve Pool': 70,
  'Genesis NFT Holders': 5,
  'Affiliates Pool': 5,
  'Progressive Jackpot': 70,
  'Marketing & Development': 50
};

const initialTreasuryPools = {
  rewardsPool: {
    name: 'Rewards Pool',
    description: 'Main rewards pool for gameplay rewards',
    volumeShare: 75,
    isLocked: false,
    balance: 10000000,
    icon: '🏆',
    maxAdjustment: 50
  },
  reservePool: {
    name: 'Reserve Pool',
    description: 'Acts as a Buffer to balance huge wins if Any',
    volumeShare: 10,
    isLocked: false,
    balance: 5000000,
    icon: '💰',
    maxAdjustment: 70
  },
  genesisHolders: {
    name: 'Genesis NFT Holders',
    description: 'Rewards for Genesis NFT holders',
    volumeShare: 5,
    isLocked: false,
    balance: 0,
    icon: '👑',
    maxAdjustment: 5
  },
  affiliatesPool: {
    name: 'Affiliates Pool',
    description: 'Rewards for affiliate partners',
    volumeShare: 5,
    isLocked: false,
    balance: 500000,
    icon: '🤝',
    maxAdjustment: 5
  },
  progressiveJackpot: {
    name: 'Progressive Jackpot',
    description: 'Accumulating jackpot for special events',
    volumeShare: 3,
    isLocked: false,
    balance: 0,
    icon: '🎰',
    maxAdjustment: 70
  },
  marketingDev: {
    name: 'Marketing & Development',
    description: 'Funds for marketing campaigns and development',
    volumeShare: 2,
    isLocked: false,
    balance: 500000,
    icon: '🚀',
    maxAdjustment: 50
  }
};

const DAODashboard = () => {
  const { publicKey } = useWallet();
  const { connection } = useConnection();
  const [activeTab, setActiveTab] = useState(0);
  const [isVoteModalOpen, setVoteModalOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [votedProposals, setVotedProposals] = useState(new Set());
  const [userVotes, setUserVotes] = useState({});
  const [proposals, setProposals] = useState([]);
  const [completedProps, setCompletedProps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdjustPoolModalOpen, setAdjustPoolModalOpen] = useState(false);
  const [selectedPool, setSelectedPool] = useState(null);
  const [adjustmentType, setAdjustmentType] = useState('increase');
  const [adjustmentValue, setAdjustmentValue] = useState(5);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [jinuPrice, setJinuPrice] = useState(0);
  const [userNfts, setUserNfts] = useState([]);
  const [treasuryPools, setTreasuryPools] = useState(initialTreasuryPools);
  const [revenueData, setRevenueData] = useState({
    totalEarned: { 
      jinu: 1500.25,
      usd: 150025.00
    },
    pendingRewards: {
      jinu: 250.75,
      usd: 25075.00
    },
    lastClaim: "2025-03-30",
    revenueHistory: [
      { date: "2025-03", amount: 500.75 },
      { date: "2025-02", amount: 450.50 },
      { date: "2025-01", amount: 549.00 }
    ]
  });

  // Function to fetch proposals from the API
  const fetchProposals = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Fetch active proposals
      const activeProposals = await daoService.getProposals({ status: 'Active' });
      setProposals(activeProposals.length > 0 ? activeProposals : mockProposals);
      
      // Fetch completed proposals
      const completedProposals = await daoService.getProposals({ 
        status: ['Approved', 'Rejected', 'Executed', 'Cancelled']
      });
      setCompletedProps(completedProposals.length > 0 ? completedProposals : completedProposals);
      
      // If user is connected, fetch their votes
      if (publicKey) {
        try {
          const token = localStorage.getItem('authToken');
          if (token) {
            const userVotesData = await daoService.getUserVotes(token);
            const votedProposalIds = new Set(userVotesData.map(vote => vote.proposalId));
            setVotedProposals(votedProposalIds);
            
            // Create a map of proposal ID to vote
            const votesMap = {};
            userVotesData.forEach(vote => {
              votesMap[vote.proposalId] = vote;
            });
            setUserVotes(votesMap);
          }
        } catch (votesError) {
          console.warn('Error fetching user votes:', votesError);
        }
      }
    } catch (error) {
      console.error('Error fetching proposals:', error);
      setError('Failed to load proposals. Please try again later.');
      // Fallback to mock data
      setProposals(mockProposals);
      setCompletedProps(completedProposals);
    } finally {
      setIsLoading(false);
    }
  }, [publicKey]);

  // Call fetchProposals when component mounts or when publicKey changes
  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  const handlePoolAdjustmentProposal = async (poolId, adjustmentType, percentage) => {
    if (!publicKey) {
      toast({
        title: "Connect Wallet",
        description: "Please connect your wallet to create proposals.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const pool = treasuryPools[poolId];
    if (!pool) return;
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error("Authentication required");
      }
      
      // Format proposal data for API
      const proposalData = {
        title: `Adjust ${pool.name} Volume Share`,
        description: `Proposal to ${adjustmentType === 'increase' ? 'increase' : 'decrease'} the volume share of ${pool.name} by ${percentage}%`,
        proposalType: 'ParameterChange',
        votingEndTime: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
      };
      
      // Create proposal via API
      const createdProposal = await daoService.createProposal(proposalData, token);
      
      // For UI purposes, add some additional properties that might not be in the API response
      const enhancedProposal = {
        ...createdProposal,
        category: "Treasury",
        activeVoters: 0,
        options: [
          { id: 1, text: "Approve", percentage: 0, votes: 0 },
          { id: 2, text: "Reject", percentage: 0, votes: 0 }
        ]
      };
      
      // Update UI
      setProposals(prev => [enhancedProposal, ...prev]);
      setAdjustPoolModalOpen(false);
      setSelectedPool(null);
      
      toast({
        title: "Proposal Created",
        description: `Your proposal to adjust ${pool.name} has been created.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error creating proposal:', error);
      toast({
        title: "Proposal Creation Failed",
        description: error.message || "Failed to create proposal. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const renderAdjustPoolModal = () => {
    const maxAdjustment = POOL_LIMITS[selectedPool?.name] || 5;
    const currentPresets = getAdjustmentPresets(selectedPool?.name, adjustmentType);

    const handlePresetClick = (value) => {
      setAdjustmentValue(value);
    };

    return (
      <Modal isOpen={isAdjustPoolModalOpen} onClose={() => setAdjustPoolModalOpen(false)}>
        <ModalOverlay bg="blackAlpha.800" backdropFilter="blur(5px)" />
        <ModalContent bg="gray.900" borderWidth="1px" borderColor="rgba(255, 215, 0, 0.3)">
          <ModalHeader pb={2}>
            <Flex justify="space-between" align="center">
              <Text>Propose Pool Adjustment</Text>
              <IconButton
                icon={<CloseIcon />}
                aria-label="Close modal"
                variant="ghost"
                color={goldColor}
                size="sm"
                onClick={() => setAdjustPoolModalOpen(false)}
                _hover={{ bg: 'rgba(255, 215, 0, 0.1)' }}
              />
            </Flex>
          </ModalHeader>

          <ModalBody>
            <VStack spacing={6} align="stretch">
              <Box>
                <Text mb={2} color="gray.400">Selected Pool</Text>
                <HStack>
                  <Text fontSize="xl" fontWeight="bold">{selectedPool?.name}</Text>
                  <Badge colorScheme={selectedPool?.isLocked ? 'red' : 'green'}>
                    {selectedPool?.isLocked ? 'Locked' : 'Adjustable'}
                  </Badge>
                </HStack>
                <Text fontSize="sm" color="gray.500" mt={1}>{selectedPool?.description}</Text>
                <Text fontSize="sm" color="gray.400" mt={2}>
                  Maximum {adjustmentType}: {maxAdjustment}%
                </Text>
              </Box>

              <Box>
                <Text mb={4} color="gray.400">Adjustment Type</Text>
                <RadioGroup value={adjustmentType} onChange={setAdjustmentType}>
                  <Stack direction="row" spacing={5}>
                    <Radio 
                      value="increase" 
                      colorScheme="green"
                      isDisabled={selectedPool?.isLocked}
                    >
                      <HStack>
                        <Icon as={FiTrendingUp} color="green.400" />
                        <Text>Increase</Text>
                      </HStack>
                    </Radio>
                    <Radio 
                      value="decrease" 
                      colorScheme="red"
                      isDisabled={selectedPool?.isLocked}
                    >
                      <HStack>
                        <Icon as={FiTrendingDown} color="red.400" />
                        <Text>Decrease</Text>
                      </HStack>
                    </Radio>
                  </Stack>
                </RadioGroup>
              </Box>

              <Box>
                <Text mb={4} color="gray.400">Quick Presets</Text>
                <Wrap spacing={2}>
                  {currentPresets.map((preset) => (
                    <WrapItem key={preset}>
                      <Button
                        size="sm"
                        variant="outline"
                        borderColor={adjustmentValue === preset ? goldColor : 'gray.600'}
                        color={adjustmentValue === preset ? goldColor : 'gray.400'}
                        onClick={() => handlePresetClick(preset)}
                        _hover={{
                          bg: 'rgba(255, 215, 0, 0.1)',
                          borderColor: goldColor,
                          color: goldColor
                        }}
                      >
                        {preset}%
                      </Button>
                    </WrapItem>
                  ))}
                </Wrap>
              </Box>

              <Box>
                <Text mb={4} color="gray.400">Adjustment Percentage</Text>
                <Slider
                  value={adjustmentValue}
                  onChange={setAdjustmentValue}
                  min={0}
                  max={maxAdjustment}
                  step={1}
                  isDisabled={selectedPool?.isLocked}
                >
                  <SliderMark value={0} mt={2} fontSize="sm" color="gray.500">
                    0%
                  </SliderMark>
                  <SliderMark 
                    value={adjustmentValue} 
                    mt={-8}
                    ml={-6}
                    bg={adjustmentType === 'increase' ? 'green.900' : 'red.900'}
                    color={adjustmentType === 'increase' ? 'green.200' : 'red.200'}
                    p={1}
                    borderRadius="md"
                    fontSize="sm"
                  >
                    {adjustmentValue}%
                  </SliderMark>
                  <SliderMark value={maxAdjustment} mt={2} ml={-8} fontSize="sm" color="gray.500">
                    {maxAdjustment}%
                  </SliderMark>
                  <SliderTrack 
                    bg="gray.700"
                  >
                    <SliderFilledTrack 
                      bg={adjustmentType === 'increase' ? 'green.500' : 'red.500'} 
                    />
                  </SliderTrack>
                  <SliderThumb 
                    boxSize={4}
                    bg={goldColor}
                    _focus={{ boxShadow: `0 0 0 3px rgba(255, 215, 0, 0.3)` }}
                  />
                </Slider>
              </Box>

              <Box>
                <Text mb={2} color="gray.400">Impact Preview</Text>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <Box>
                    <Text fontSize="sm" color="gray.500">Current Share</Text>
                    <Text fontSize="xl" fontWeight="bold">{selectedPool?.volumeShare}%</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.500">After Adjustment</Text>
                    <Text 
                      fontSize="xl" 
                      fontWeight="bold"
                      color={adjustmentType === 'increase' ? 'green.400' : 'red.400'}
                    >
                      {adjustmentType === 'increase' 
                        ? selectedPool?.volumeShare + adjustmentValue
                        : selectedPool?.volumeShare - adjustmentValue}%
                    </Text>
                  </Box>
                </Grid>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing={3}>
              <Button
                variant="ghost"
                onClick={() => setAdjustPoolModalOpen(false)}
                color="gray.400"
                _hover={{ bg: 'whiteAlpha.100' }}
              >
                Cancel
              </Button>
              <PirateButton
                leftIcon={<EditIcon />}
                onClick={() => handlePoolAdjustmentProposal(selectedPool?.id, adjustmentType, adjustmentValue)}
                isDisabled={selectedPool?.isLocked || adjustmentValue === 0}
              >
                Create Proposal
              </PirateButton>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  };

  const renderPoolCard = (key, pool) => (
    <PirateCard key={key}>
      <CardHeader>
        <HStack justify="space-between">
          <VStack align="start" spacing={1}>
            <HStack>
              <Text>{pool.icon}</Text>
              <Text fontSize="lg" fontWeight="bold">{pool.name}</Text>
            </HStack>
            <HStack>
              <Text fontSize="2xl" fontWeight="bold" color={goldColor}>
                {pool.balance.toLocaleString()} JINU
              </Text>
            </HStack>
            <Text fontSize="sm" color="gray.500">
              ${(pool.balance * 0.01).toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </Text>
          </VStack>
          <Badge
            colorScheme={pool.isLocked ? 'red' : 'green'}
            variant="subtle"
          >
            {pool.isLocked ? 'LOCKED' : 'ADJUSTABLE'}
          </Badge>
        </HStack>
      </CardHeader>

      <CardBody pt={4}>
        <VStack align="stretch" spacing={4}>
          <Box>
            <Text fontSize="sm" color="gray.400" mb={2}>Volume Share</Text>
            <HStack justify="space-between">
              <Text fontSize="xl" fontWeight="bold">{pool.volumeShare}%</Text>
              <Text fontSize="sm" color="gray.400">
                Max Adjustment: ±{pool.maxAdjustment}%
              </Text>
            </HStack>
          </Box>

          <Box>
            <Text fontSize="sm" color="gray.400" mb={2}>Description</Text>
            <Text fontSize="sm" color="gray.500" noOfLines={2}>
              {pool.description}
            </Text>
          </Box>

          {!pool.isLocked && (
            <Button
              variant="outline"
              size="sm"
              rightIcon={<EditIcon />}
              onClick={() => {
                setSelectedPool({...pool, id: key, maxAdjustment: pool.maxAdjustment});
                setAdjustPoolModalOpen(true);
              }}
              borderColor={goldColor}
              color={goldColor}
              _hover={{
                bg: 'rgba(255, 215, 0, 0.1)',
              }}
            >
              Propose Adjustment
            </Button>
          )}
        </VStack>
      </CardBody>
    </PirateCard>
  );

  const renderTreasuryTab = () => {
    return (
      <Box p={6}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {Object.entries(initialTreasuryPools).map(([key, pool]) => renderPoolCard(key, pool))}
        </SimpleGrid>
        {renderAdjustPoolModal()}
      </Box>
    );
  };

  const renderMyNFTsTab = () => {
    if (!publicKey) {
      return (
        <Center p={8}>
          <VStack spacing={4}>
            <Text>Connect your wallet to view your Angels Key NFTs</Text>
            <Button
              leftIcon={<Icon as={GiPirateCaptain} />}
              onClick={() => toast({
                title: "Coming Soon",
                description: "This feature will be available in the next update",
                status: "info",
                duration: 3000,
                isClosable: true,
              })}
            >
              Captain's Log
            </Button>
          </VStack>
        </Center>
      );
    }

    return (
      <Box>
        {/* Stats Overview */}
        <StatsOverview stats={userStats} />
        
        {/* NFT Cards */}
        <VStack spacing={6} align="stretch" mt={6}>
          {userNfts.map((nft) => (
            <GoldenKeyNFTCard key={nft.id} nft={nft} />
          ))}
        </VStack>
      </Box>
    );
  };

  // State for JINU price and treasury data
  const [treasuryData, setTreasuryData] = useState({});

  // Fetch JINU price from CoinGecko
  useEffect(() => {
    // Use a static JINU price to prevent API rate limiting and constant refreshing
    const mockJinuPrice = 0.028;
    setJinuPrice(mockJinuPrice);
    
    // Comment out the actual API call during development to prevent refreshing
    /*
    const fetchJinuPrice = async () => {
      try {
        const response = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=jinu&vs_currencies=usd`);
        const data = await response.json();
        setJinuPrice(data.jinu.usd);
      } catch (error) {
        console.error('Error fetching JINU price:', error);
      }
    };

    fetchJinuPrice();
    const interval = setInterval(fetchJinuPrice, 60000); // Update every minute
    return () => clearInterval(interval);
    */
  }, []);

  // Set static treasury data to prevent continuous changes
  useEffect(() => {
    // Create static treasury data with fixed values
    const staticTreasuryData = {};
    
    Object.entries(initialTreasuryPools).forEach(([key, pool]) => {
      const usdValue = pool.balance * jinuPrice;
      // Use fixed values for change percentages
      const fixedChanges = {
        rewardsPool: 2.5,
        reservePool: 1.8,
        devMarketingFund: 3.2,
        affiliatesPool: 1.5,
        progressiveJackpot: 4.0,
        marketingDevelopment: 2.2
      };

      staticTreasuryData[key] = {
        jinu: pool.balance,
        usd: usdValue,
        change: fixedChanges[key] || 0,
      };
    });

    setTreasuryData(staticTreasuryData);
  }, [jinuPrice]); // Only recalculate when jinuPrice is set initially

  // Mock data
  const [daoStats] = useState({
    totalProposals: mockProposals.length,
    activeProposals: mockProposals.filter(p => p.status === 'active').length,
    totalVoters: 777,
    activeVoters: 521,
    participationRate: '67.1%',
    passRate: '82%'
  });

  useEffect(() => {
    // Only show user's NFTs when wallet is connected
    if (publicKey) {
      // Use static NFT data to prevent continuous refreshing
      const generateMockNFTs = () => {
        const rarities = ['Legendary', 'Mythic', 'Rare', 'Uncommon', 'Common'];
        const mockNFTs = [];
        
        // Use fixed seed values for random numbers to prevent changes on refresh
        const fixedStakingDays = [45, 67, 23];
        const fixedEarnings = [
          Array.from({length: 30}, (_, i) => 120 + (i % 5) * 10),
          Array.from({length: 30}, (_, i) => 90 + (i % 3) * 15),
          Array.from({length: 30}, (_, i) => 150 + (i % 4) * 8)
        ];
        
        for (let i = 0; i < 3; i++) { // Show 3 NFTs for demo
          const stakingDays = fixedStakingDays[i];
          const dailyEarnings = fixedEarnings[i];
          
          mockNFTs.push({
            id: (777 - i).toString(), // Count down from 777
            rarity: rarities[Math.floor(Math.random() * rarities.length)],
            totalEarned: dailyEarnings.reduce((a, b) => a + b, 0), // Sum of daily earnings
            lastReward: dailyEarnings[dailyEarnings.length - 1], // Last day's earning
            stakingDays: stakingDays,
            stakingTotal: 90,
            isStaked: stakingDays > 0
          });
        }
        return mockNFTs;
      };

      setUserNfts(generateMockNFTs());
    } else {
      setUserNfts([]);
    }
  }, [publicKey]);

  // Calculate total voting power
  const totalVotingPower = userNfts.reduce((total, nft) => total + nft.votingPower, 0);

  // Calculate user NFT stats
  // Use static values for user NFT stats to prevent changing values on refresh
  const getUserNFTStats = () => {
    // Return static values that match the screenshots
    return {
      totalNFTs: 3,
      totalEarned: {
        jinu: 1171259.45,
        usd: 15929.13
      },
      lastPeriod: {
        jinu: 99476.83,
        usd: 1352.88
      },
      dailyAverage: {
        jinu: 1054.48,
        usd: 14.34
      }
    };
  };

  // Store the static stats in state to ensure they don't change
  const [userStats] = useState(getUserNFTStats());

  // UI Colors
  const bgGradient = "linear(to-br, #0D1019, #1A202C)";
  const darkBg = "#0D1019";
  const cardBg = "rgba(13, 16, 25, 0.7)";
  
  const handleGateAuthenticated = () => {
    setIsAuthenticated(true);
    toast({
      title: "Access Granted",
      description: "Welcome to the Angels Key DAO Dashboard",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
  };

  // Function to handle voting
  const handleVote = async (proposalId, vote) => {
    if (!publicKey) {
      toast({
        title: "Connect Wallet",
        description: "Please connect your wallet to vote.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (votedProposals.has(proposalId)) {
      toast({
        title: "Already Voted",
        description: "You have already voted on this proposal.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error("Authentication required");
      }
      
      // Convert vote string to boolean for API
      const inFavor = vote === 'approve';
      
      const voteData = {
        inFavor,
        amount: 1 // Assuming 1 vote per user, adjust as needed
      };
      
      // Call API to record vote
      await daoService.voteOnProposal(proposalId, voteData, token);
      
      // Update local state
      setVotedProposals(prev => new Set([...prev, proposalId]));
      setUserVotes(prev => ({
        ...prev,
        [proposalId]: vote
      }));
      
      // Update the proposal data in the UI immediately for better UX
      setProposals(currentProposals => 
        currentProposals.map(proposal => {
          if (proposal.id === proposalId || proposal.proposalId === proposalId) {
            const options = proposal.options ? proposal.options.map(option => {
              if ((vote === 'approve' && option.id === 1) || (vote === 'reject' && option.id === 2)) {
                return {
                  ...option,
                  votes: option.votes + 1
                };
              }
              return option;
            }) : [];

            // If using the API data structure which might be different
            if (!proposal.options) {
              // Update votes based on API structure
              return {
                ...proposal,
                votesFor: inFavor ? (proposal.votesFor + 1) : proposal.votesFor,
                votesAgainst: !inFavor ? (proposal.votesAgainst + 1) : proposal.votesAgainst
              };
            }

            // Recalculate percentages for UI options
            const totalVotes = options.reduce((sum, opt) => sum + opt.votes, 0);
            const updatedOptions = options.map(opt => ({
              ...opt,
              percentage: totalVotes > 0 ? Math.round((opt.votes / totalVotes) * 100) : 0
            }));

            return {
              ...proposal,
              activeVoters: proposal.activeVoters + 1,
              options: updatedOptions
            };
          }
          return proposal;
        })
      );
      
      // Fetch the updated proposal to ensure UI is in sync with backend
      try {
        const updatedProposal = await daoService.getProposalById(proposalId);
        if (updatedProposal) {
          setProposals(prev => prev.map(p => 
            (p.id === proposalId || p.proposalId === proposalId) ? updatedProposal : p
          ));
        }
      } catch (fetchError) {
        console.warn('Error fetching updated proposal:', fetchError);
      }

      // Show success message
      toast({
        title: "Vote Recorded",
        description: `Your ${vote} vote has been recorded.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error voting on proposal:', error);
      toast({
        title: "Vote Failed",
        description: error.message || "Failed to record your vote. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Check if user has voted on a proposal
  const hasVoted = (proposalId) => votedProposals.has(proposalId);

  // Get user's vote for a proposal
  const getUserVote = (proposalId) => userVotes[proposalId];

  // Helper function for category colors
  const getCategoryColor = (category) => {
    switch (category) {
      case 'Treasury':
        return 'purple';
      case 'Development':
        return 'blue';
      case 'Community':
        return 'orange';
      case 'Partnership':
        return 'cyan';
      case 'Economics':
        return 'pink';
      case 'Staking':
        return 'yellow';
      case 'Technology':
        return 'green';
      default:
        return 'gray';
    }
  };

  // Render NFTs tab content
  const renderNFTsTab = () => {
    if (!publicKey) {
      return (
        <Center p={8}>
          <VStack spacing={4}>
            <Text>Connect your wallet to view your Angels Key NFTs</Text>
            <Button
              leftIcon={<Icon as={GiPirateCaptain} />}
              onClick={() => toast({
                title: "Coming Soon",
                description: "This feature will be available in the next update",
                status: "info",
                duration: 3000,
                isClosable: true,
              })}
            >
              Captain's Log
            </Button>
          </VStack>
        </Center>
      );
    }

    return (
      <Box>
        {/* Stats Overview */}
        <StatsOverview stats={userStats} />
        
        {/* NFT Cards */}
        <VStack spacing={6} align="stretch" mt={6}>
          {userNfts.map((nft) => (
            <GoldenKeyNFTCard key={nft.id} nft={nft} />
          ))}
        </VStack>
      </Box>
    );
  };

  if (!isAuthenticated) {
    return <AngelsKeyGate onAuthenticated={handleGateAuthenticated} />;
  }

  return (
    <Box>
      {/* <AngelsKeyGate onAuthenticated={handleGateAuthenticated} /> */}
      
      <Box 
        position="relative" 
        minH="100vh" 
        bgGradient={bgGradient}
        backgroundImage="url('/images/pirate-map-bg.jpg')" 
        backgroundSize="cover" 
        backgroundPosition="center"
        backgroundBlendMode="overlay"
        pt={20} 
        pb={12}
        px={4}
      >
        {/* Decorative Pirate Icons in Background */}
        <Box position="absolute" top="20px" left="20px" opacity="0.15" zIndex="0">
          <Box as={GiCompass} color={goldColor} boxSize={12} />
        </Box>
        <Box position="absolute" bottom="20px" right="20px" opacity="0.15" zIndex="0">
          <Box as={GiAnchor} color={goldColor} boxSize={12} />
        </Box>
        <Box position="absolute" top="20px" right="20px" opacity="0.15" zIndex="0">
          <Box as={GiShipWheel} color={goldColor} boxSize={12} />
        </Box>
        <Box position="absolute" bottom="20px" left="20px" opacity="0.15" zIndex="0">
          <Box as={GiTreasureMap} color={goldColor} boxSize={12} />
        </Box>
        <Box position="absolute" top="50%" left="20px" opacity="0.15" zIndex="0" transform="translateY(-50%)">
          <Box as={GiPirateSkull} color={goldColor} boxSize={12} />
        </Box>
        <Box position="absolute" top="50%" right="20px" opacity="0.15" zIndex="0" transform="translateY(-50%)">
          <Box as={GiChest} color={goldColor} boxSize={12} />
        </Box>
        
        <Flex 
          direction="column" 
          maxW="1400px" 
          mx="auto" 
          position="relative" 
          zIndex="1"
        >
          {/* Header with Title and Wallet */}
          <Flex 
            justify="space-between" 
            align="center" 
            mb={8} 
            pb={4} 
            borderBottomWidth="1px" 
            borderBottomColor="rgba(255, 255, 255, 0.1)" 
            borderColor="rgba(255, 215, 0, 0.3)"
          >
            <HStack spacing={3}>
              <Icon as={GiPirateFlag} color={goldColor} boxSize={8} />
              <Heading 
                color="white" 
                size="xl" 
                fontFamily="Aclonica, sans-serif"
                textShadow="0 0 10px rgba(255, 215, 0, 0.5)"
              >
                Palm Heist DAO
              </Heading>
              <Badge 
                px={3} 
                py={1} 
                borderRadius="full" 
                bg="rgba(255, 215, 0, 0.2)" 
                color={goldColor}
                borderWidth="1px"
                borderColor={goldColor}
              >
                Beta
              </Badge>
            </HStack>
            
            <HStack spacing={4}>
              <PirateButton 
                leftIcon={<Icon as={GiPirateCaptain} />}
                onClick={() => toast({
                  title: "Coming Soon",
                  description: "This feature will be available in the next update",
                  status: "info",
                  duration: 3000,
                  isClosable: true,
                })}
              >
                Captain's Log
              </PirateButton>
              <Box>
                <style>
                  {`
                    .wallet-adapter-button {
                      background: transparent !important;
                      border: 2px solid ${goldColor} !important;
                      color: ${goldColor} !important;
                      border-radius: 25px !important;
                      padding: 0 24px !important;
                      height: 40px !important;
                      font-size: 16px !important;
                      font-weight: 500 !important;
                      transition: all 0.3s ease !important;
                    }
                    .wallet-adapter-button:hover {
                      background: rgba(255, 215, 0, 0.1) !important;
                      transform: translateY(-2px);
                      box-shadow: 0 4px 12px rgba(255, 215, 0, 0.3);
                    }
                    .wallet-adapter-dropdown-list {
                      background: #1A1B1F !important;
                      border: 1px solid rgba(255, 215, 0, 0.3) !important;
                    }
                    .wallet-adapter-dropdown-list-item {
                      background: transparent !important;
                      color: ${goldColor} !important;
                      font-size: 14px !important;
                      padding: 8px 16px !important;
                    }
                    .wallet-adapter-dropdown-list-item:hover {
                      background: rgba(255, 215, 0, 0.1) !important;
                    }
                  `}
                </style>
                {/* Removed extra wallet button */}
              </Box>
            </HStack>
          </Flex>
          
          {/* Main Dashboard Content */}
          <Tabs variant="unstyled" colorScheme="green">
            <TabList mb={6} borderBottom="1px solid" borderColor="rgba(255, 255, 255, 0.1)" pb={2}>
              <PirateTab>
                <HStack>
                  <Icon as={GiPirateFlag} />
                  <Text>Overview</Text>
                </HStack>
              </PirateTab>
              <PirateTab>
                <Text fontSize="xl">🏛️</Text>
                <Text ml={2}>Governance</Text>
              </PirateTab>
              <PirateTab>
                <Text fontSize="xl">💰</Text>
                <Text ml={2}>Treasury</Text>
              </PirateTab>
              <PirateTab>
                <HStack>
                  <Icon as={GiChest} />
                  <Text>My NFTs</Text>
                </HStack>
              </PirateTab>
              <PirateTab>
                <HStack>
                  <Icon as={GiMoneyStack} />
                  <Text>Revenue</Text>
                </HStack>
              </PirateTab>
            </TabList>
            
            <TabPanels>
              <TabPanel>
                {/* NFT Holdings and Voting Power Section */}
                {publicKey && (
                  <PirateCard mb={6}>
                    <CardHeader>
                      <Heading size="md">Your Angels Keys</Heading>
                    </CardHeader>
                    <CardBody>
                      <Box mb={6}>
                        <StatsOverview stats={userStats} />
                      </Box>
                      
                      {/* Display 3 NFT keys in a row */}
                      <Box mb={6}>
                        <Heading size="md" mb={4}>Your Angels Keys</Heading>
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                          {/* Angels Key #1 */}
                          <Box
                            position="relative"
                            borderRadius="lg"
                            overflow="hidden"
                            bg="rgba(0, 0, 0, 0.3)"
                            border="2px solid"
                            borderColor="rgba(255, 215, 0, 0.3)"
                            _hover={{
                              borderColor: "rgba(255, 215, 0, 0.6)",
                              transform: "translateY(-2px)",
                              transition: "all 0.2s"
                            }}
                          >
                            <Box
                              position="relative"
                              paddingBottom="80%" // Makes it shorter
                            >
                              <Image
                                position="absolute"
                                top={0}
                                left={0}
                                width="100%"
                                height="100%"
                                objectFit="contain"
                                src="/images/angels-key.gif"
                                alt="Angels Key NFT"
                                borderRadius="lg"
                                p={3}
                              />
                            </Box>
                            <Box p={3} bg="rgba(0, 0, 0, 0.7)" borderTop="1px solid" borderColor="rgba(255, 215, 0, 0.3)">
                              <Text fontWeight="bold" color="gold" fontSize="md">Angels Key #777</Text>
                              <Text fontSize="sm" color="whiteAlpha.800">Voting Power: 100</Text>
                            </Box>
                          </Box>
                          
                          {/* Angels Key #2 */}
                          <Box
                            position="relative"
                            borderRadius="lg"
                            overflow="hidden"
                            bg="rgba(0, 0, 0, 0.3)"
                            border="2px solid"
                            borderColor="rgba(255, 215, 0, 0.3)"
                            _hover={{
                              borderColor: "rgba(255, 215, 0, 0.6)",
                              transform: "translateY(-2px)",
                              transition: "all 0.2s"
                            }}
                          >
                            <Box
                              position="relative"
                              paddingBottom="80%" // Makes it shorter
                            >
                              <Image
                                position="absolute"
                                top={0}
                                left={0}
                                width="100%"
                                height="100%"
                                objectFit="contain"
                                src="/images/angels-key.gif"
                                alt="Angels Key NFT"
                                borderRadius="lg"
                                p={3}
                              />
                            </Box>
                            <Box p={3} bg="rgba(0, 0, 0, 0.7)" borderTop="1px solid" borderColor="rgba(255, 215, 0, 0.3)">
                              <Text fontWeight="bold" color="gold" fontSize="md">Angels Key #776</Text>
                              <Text fontSize="sm" color="whiteAlpha.800">Voting Power: 100</Text>
                            </Box>
                          </Box>
                          
                          {/* Angels Key #3 */}
                          <Box
                            position="relative"
                            borderRadius="lg"
                            overflow="hidden"
                            bg="rgba(0, 0, 0, 0.3)"
                            border="2px solid"
                            borderColor="rgba(255, 215, 0, 0.3)"
                            _hover={{
                              borderColor: "rgba(255, 215, 0, 0.6)",
                              transform: "translateY(-2px)",
                              transition: "all 0.2s"
                            }}
                          >
                            <Box
                              position="relative"
                              paddingBottom="80%" // Makes it shorter
                            >
                              <Image
                                position="absolute"
                                top={0}
                                left={0}
                                width="100%"
                                height="100%"
                                objectFit="contain"
                                src="/images/angels-key.gif"
                                alt="Angels Key NFT"
                                borderRadius="lg"
                                p={3}
                              />
                            </Box>
                            <Box p={3} bg="rgba(0, 0, 0, 0.7)" borderTop="1px solid" borderColor="rgba(255, 215, 0, 0.3)">
                              <Text fontWeight="bold" color="gold" fontSize="md">Angels Key #775</Text>
                              <Text fontSize="sm" color="whiteAlpha.800">Voting Power: 100</Text>
                            </Box>
                          </Box>
                        </SimpleGrid>
                      </Box>
                    </CardBody>
                  </PirateCard>
                )}
                
                {/* Treasury Overview */}
                  <PirateCard mb={8}>
                    <CardHeader>
                      <Flex align="center">
                        <Icon as={GiChest} color={goldColor} boxSize={6} mr={2} />
                        <Heading size="md">Treasury Overview</Heading>
                      </Flex>
                    </CardHeader>
                    <CardBody>
                      {/* Display 6 treasury boxes in a grid (3x2) */}
                      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                        {/* Rewards Pool */}
                        <Box 
                          p={4} 
                          bg="rgba(0, 0, 0, 0.3)" 
                          borderRadius="lg" 
                          border="1px solid" 
                          borderColor="rgba(255, 215, 0, 0.3)"
                        >
                          <Text fontWeight="bold" color="gold" mb={2}>Rewards Pool</Text>
                          <Text fontSize="2xl" color="#32CD32">
                            {treasuryData.rewardsPool?.jinu?.toLocaleString(undefined, { maximumFractionDigits: 2 })} JINU
                          </Text>
                          <Stat>
                            <StatHelpText color="#32CD32">
                              <StatArrow type="increase" />
                              {Math.abs(treasuryData.rewardsPool?.change || 0).toFixed(2)}%
                            </StatHelpText>
                          </Stat>
                          <Text color="#32CD32" fontSize="sm">
                            ${(treasuryData.rewardsPool?.usd || 0).toFixed(2)} USD
                          </Text>
                          <Text fontSize="xs" color="gray.400" mt={2}>Main rewards pool for gameplay rewards</Text>
                        </Box>
                        
                        {/* Reserve Pool */}
                        <Box 
                          p={4} 
                          bg="rgba(0, 0, 0, 0.3)" 
                          borderRadius="lg" 
                          border="1px solid" 
                          borderColor="rgba(255, 215, 0, 0.3)"
                        >
                          <Text fontWeight="bold" color="gold" mb={2}>Reserve Pool</Text>
                          <Text fontSize="2xl" color="#32CD32">
                            {treasuryData.reservePool?.jinu?.toLocaleString(undefined, { maximumFractionDigits: 2 })} JINU
                          </Text>
                          <Stat>
                            <StatHelpText color="#32CD32">
                              <StatArrow type="increase" />
                              {Math.abs(treasuryData.reservePool?.change || 0).toFixed(2)}%
                            </StatHelpText>
                          </Stat>
                          <Text color="#32CD32" fontSize="sm">
                            ${(treasuryData.reservePool?.usd || 0).toFixed(2)} USD
                          </Text>
                          <Text fontSize="xs" color="gray.400" mt={2}>Acts as a buffer to balance huge wins</Text>
                        </Box>
                        
                        {/* Development Fund */}
                        <Box 
                          p={4} 
                          bg="rgba(0, 0, 0, 0.3)" 
                          borderRadius="lg" 
                          border="1px solid" 
                          borderColor="rgba(255, 215, 0, 0.3)"
                        >
                          <Text fontWeight="bold" color="gold" mb={2}>Dev & Marketing</Text>
                          <Text fontSize="2xl" color="#32CD32">
                            {treasuryData.devMarketingFund?.jinu?.toLocaleString(undefined, { maximumFractionDigits: 2 })} JINU
                          </Text>
                          <Stat>
                            <StatHelpText color="#32CD32">
                              <StatArrow type="increase" />
                              {Math.abs(treasuryData.devMarketingFund?.change || 0).toFixed(2)}%
                            </StatHelpText>
                          </Stat>
                          <Text color="#32CD32" fontSize="sm">
                            ${(treasuryData.devMarketingFund?.usd || 0).toFixed(2)} USD
                          </Text>
                          <Text fontSize="xs" color="gray.400" mt={2}>For ongoing development and marketing</Text>
                        </Box>
                        
                        {/* Affiliates Pool */}
                        <Box 
                          p={4} 
                          bg="rgba(0, 0, 0, 0.3)" 
                          borderRadius="lg" 
                          border="1px solid" 
                          borderColor="rgba(255, 215, 0, 0.3)"
                        >
                          <Text fontWeight="bold" color="gold" mb={2}>Affiliates Pool</Text>
                          <Text fontSize="2xl" color="#32CD32">
                            {treasuryData.affiliatesPool?.jinu?.toLocaleString(undefined, { maximumFractionDigits: 2 })} JINU
                          </Text>
                          <Stat>
                            <StatHelpText color="#32CD32">
                              <StatArrow type="increase" />
                              {Math.abs(treasuryData.affiliatesPool?.change || 0).toFixed(2)}%
                            </StatHelpText>
                          </Stat>
                          <Text color="#32CD32" fontSize="sm">
                            ${(treasuryData.affiliatesPool?.usd || 0).toFixed(2)} USD
                          </Text>
                          <Text fontSize="xs" color="gray.400" mt={2}>Rewards for affiliate partners</Text>
                        </Box>
                        
                        {/* Progressive Jackpot */}
                        <Box 
                          p={4} 
                          bg="rgba(0, 0, 0, 0.3)" 
                          borderRadius="lg" 
                          border="1px solid" 
                          borderColor="rgba(255, 215, 0, 0.3)"
                        >
                          <Text fontWeight="bold" color="gold" mb={2}>Progressive Jackpot</Text>
                          <Text fontSize="2xl" color="#32CD32">
                            {treasuryData.progressiveJackpot?.jinu?.toLocaleString(undefined, { maximumFractionDigits: 2 })} JINU
                          </Text>
                          <Stat>
                            <StatHelpText color="#32CD32">
                              <StatArrow type="increase" />
                              {Math.abs(treasuryData.progressiveJackpot?.change || 0).toFixed(2)}%
                            </StatHelpText>
                          </Stat>
                          <Text color="#32CD32" fontSize="sm">
                            ${(treasuryData.progressiveJackpot?.usd || 0).toFixed(2)} USD
                          </Text>
                          <Text fontSize="xs" color="gray.400" mt={2}>Growing jackpot for lucky winners</Text>
                        </Box>
                        
                        {/* Marketing & Development */}
                        <Box 
                          p={4} 
                          bg="rgba(0, 0, 0, 0.3)" 
                          borderRadius="lg" 
                          border="1px solid" 
                          borderColor="rgba(255, 215, 0, 0.3)"
                        >
                          <Text fontWeight="bold" color="gold" mb={2}>Marketing Fund</Text>
                          <Text fontSize="2xl" color="#32CD32">
                            {treasuryData.marketingDevelopment?.jinu?.toLocaleString(undefined, { maximumFractionDigits: 2 })} JINU
                          </Text>
                          <Stat>
                            <StatHelpText color="#32CD32">
                              <StatArrow type="increase" />
                              {Math.abs(treasuryData.marketingDevelopment?.change || 0).toFixed(2)}%
                            </StatHelpText>
                          </Stat>
                          <Text color="#32CD32" fontSize="sm">
                            ${(treasuryData.marketingDevelopment?.usd || 0).toFixed(2)} USD
                          </Text>
                          <Text fontSize="xs" color="gray.400" mt={2}>For marketing campaigns and partnerships</Text>
                        </Box>
                      </SimpleGrid>
                    </CardBody>
                  </PirateCard>
                  

                
                {/* Revenue Distribution - positioned below Treasury Overview */}
                <PirateCard mb={8}>
                    <CardHeader>
                      <Flex align="center">
                        <Icon as={GiMoneyStack} color={goldColor} boxSize={6} mr={2} />
                        <Heading size="md">Revenue Distribution</Heading>
                      </Flex>
                    </CardHeader>
                    <CardBody>
                      <VStack spacing={4} align="stretch">
                        <RevenueDisplay data={revenueData} />
                      </VStack>
                    </CardBody>
                  </PirateCard>
                
                {/* Proposals Tabs */}
                <PirateCard mb={8}>
                  <CardHeader>
                    <Heading size="md">Governance Proposals</Heading>
                  </CardHeader>
                  <CardBody>
                    <Tabs colorScheme="teal" isLazy>
                      <TabList>
                        <PirateTab>Active Proposals ({proposals.filter(p => p.status === 'active').length})</PirateTab>
                        <PirateTab>Completed Proposals ({completedProps.length})</PirateTab>
                      </TabList>
                      
                      <TabPanels>
                        {/* Active Proposals */}
                        <TabPanel px={0}>
                          {isLoading ? (
                            <Center py={10}>
                              <VStack spacing={4}>
                                <Spinner size="xl" color="gold" thickness="4px" />
                                <Text color="gray.400">Loading proposals...</Text>
                              </VStack>
                            </Center>
                          ) : error ? (
                            <Center py={10}>
                              <VStack spacing={4}>
                                <Icon as={WarningIcon} boxSize={10} color="red.500" />
                                <Text color="red.400">{error}</Text>
                                <Button onClick={fetchProposals} colorScheme="yellow" size="sm">
                                  Try Again
                                </Button>
                              </VStack>
                            </Center>
                          ) : proposals.filter(p => p.status === 'active').length > 0 ? (
                            <VStack spacing={4} align="stretch">
                              {proposals.filter(p => p.status === 'active').map(proposal => (
                                <PirateCard key={proposal.id} variant="outline" bg="gray.900">
                                  <CardHeader pb={2}>
                                    <Flex justifyContent="space-between" alignItems="center">
                                      <Badge 
                                        colorScheme={getCategoryColor(proposal.category)}
                                        bg={
                                          proposal.category === 'Staking' 
                                            ? 'rgba(255, 255, 0, 0.2)' 
                                            : proposal.category === 'Technology'
                                            ? 'rgba(132, 204, 22, 0.2)'
                                            : undefined
                                        }
                                        color={
                                          proposal.category === 'Staking' 
                                            ? 'yellow.300' 
                                            : proposal.category === 'Technology'
                                            ? 'lime.300'
                                            : undefined
                                        }
                                      >
                                        {proposal.category}
                                      </Badge>
                                      <Badge 
                                        colorScheme="green" 
                                        variant="solid" 
                                        borderRadius="full"
                                        bg="rgba(34, 197, 94, 0.3)"
                                        color="green.300"
                                        px={3}
                                        py={1}
                                        fontSize="xs"
                                      >
                                        Active
                                      </Badge>
                                    </Flex>
                                  </CardHeader>
                                  <CardBody pt={2}>
                                    <Heading size="md" mb={3} color="white">{proposal.title}</Heading>
                                    <Text fontSize="sm" mb={3} color="gray.400" noOfLines={2}>{proposal.description}</Text>
                                    <HStack fontSize="xs" color="gray.500" mb={4} spacing={2}>
                                      <Text>Created by: {proposal.creator}</Text>
                                      <Text>•</Text>
                                      <Text>Ends: {proposal.endDate}</Text>
                                    </HStack>
                                    
                                    <Box mb={4} position="relative">
                                      {/* Progress Bar */}
                                      <Box
                                        w="100%"
                                        h="8px"
                                        position="relative"
                                        borderRadius="full"
                                        overflow="hidden"
                                      >
                                        {/* Red Background (Reject) */}
                                        <Box
                                          position="absolute"
                                          left="0"
                                          w={`${proposal.options[1].percentage}%`}
                                          h="100%"
                                          bg="red.300"
                                          opacity="0.5"
                                        />
                                        {/* Green Background (Approve) */}
                                        <Box
                                          position="absolute"
                                          right="0"
                                          w={`${proposal.options[0].percentage}%`}
                                          h="100%"
                                          bg="green.300"
                                          opacity="0.5"
                                        />
                                      </Box>

                                      {/* Vote Information */}
                                      <Flex justify="space-between" mt={2}>
                                        <Box>
                                          <Text fontSize="sm" color="red.300" fontWeight="bold">
                                            Reject ({proposal.options[1].percentage}%)
                                          </Text>
                                          <Text fontSize="xs" color="gray.500">
                                            {proposal.options[1].votes} votes
                                          </Text>
                                        </Box>
                                        <Box textAlign="right">
                                          <Text fontSize="sm" color="green.300" fontWeight="bold">
                                            Approve ({proposal.options[0].percentage}%)
                                          </Text>
                                          <Text fontSize="xs" color="gray.500">
                                            {proposal.options[0].votes} votes
                                          </Text>
                                        </Box>
                                      </Flex>
                                    </Box>

                                    {hasVoted(proposal.id) ? (
                                      <Box 
                                        p={4} 
                                        textAlign="center" 
                                        bg="rgba(52, 211, 153, 0.1)"
                                        borderRadius="lg"
                                        border="1px solid"
                                        borderColor={getUserVote(proposal.id) === 'approve' ? 'green.300' : 'red.300'}
                                      >
                                        <Text 
                                          color={getUserVote(proposal.id) === 'approve' ? 'green.300' : 'red.300'}
                                          fontWeight="medium"
                                        >
                                          You voted to {getUserVote(proposal.id)} this proposal
                                        </Text>
                                      </Box>
                                    ) : (
                                      <HStack spacing={4} justify="center">
                                        <Button
                                          leftIcon={<CloseIcon />}
                                          bg="transparent"
                                          color="red.300"
                                          flex={1}
                                          maxW="200px"
                                          fontSize="sm"
                                          h="40px"
                                          border="1px solid"
                                          borderColor="red.300"
                                          borderRadius="full"
                                          boxShadow="0 0 10px rgba(254, 78, 78, 0.2)"
                                          transition="all 0.2s"
                                          onClick={() => handleVote(proposal.id, 'reject')}
                                          _hover={{
                                            bg: "rgba(254, 78, 78, 0.1)",
                                            boxShadow: "0 0 15px rgba(254, 78, 78, 0.3)"
                                          }}
                                          _active={{
                                            bg: "rgba(254, 78, 78, 0.15)"
                                          }}
                                        >
                                          Reject
                                        </Button>
                                        <Button
                                          leftIcon={<CheckIcon />}
                                          bg="transparent"
                                          color="green.300"
                                          flex={1}
                                          maxW="200px"
                                          fontSize="sm"
                                          h="40px"
                                          border="1px solid"
                                          borderColor="green.300"
                                          borderRadius="full"
                                          boxShadow="0 0 10px rgba(52, 211, 153, 0.2)"
                                          transition="all 0.2s"
                                          onClick={() => handleVote(proposal.id, 'approve')}
                                          _hover={{
                                            bg: "rgba(52, 211, 153, 0.1)",
                                            boxShadow: "0 0 15px rgba(52, 211, 153, 0.3)"
                                          }}
                                          _active={{
                                            bg: "rgba(52, 211, 153, 0.15)"
                                          }}
                                        >
                                          Approve
                                        </Button>
                                      </HStack>
                                    )}
                                  </CardBody>
                                </PirateCard>
                              ))}
                            </VStack>
                          ) : (
                            <Text color={grayTextColor}>No active proposals at the moment.</Text>
                          )}
                        </TabPanel>
                        
                        {/* Completed Proposals */}
                        <TabPanel px={0}>
                          {isLoading ? (
                            <Center py={10}>
                              <VStack spacing={4}>
                                <Spinner size="xl" color="gold" thickness="4px" />
                                <Text color="gray.400">Loading completed proposals...</Text>
                              </VStack>
                            </Center>
                          ) : error ? (
                            <Center py={10}>
                              <VStack spacing={4}>
                                <Icon as={WarningIcon} boxSize={10} color="red.500" />
                                <Text color="red.400">{error}</Text>
                                <Button onClick={fetchProposals} colorScheme="yellow" size="sm">
                                  Try Again
                                </Button>
                              </VStack>
                            </Center>
                          ) : completedProps.length > 0 ? (
                            <VStack spacing={4} align="stretch">
                              {completedProps.map(proposal => (
                                <PirateCard key={proposal.id} variant="outline" bg="gray.900">
                                  <CardHeader pb={2}>
                                    <Flex justifyContent="space-between" alignItems="center">
                                      <VStack align="start" spacing={1}>
                                        <HStack>
                                          <Badge colorScheme={proposal.result === "Approved" ? "green" : "red"}>
                                            {proposal.result}
                                          </Badge>
                                          <Badge colorScheme="purple">{proposal.category}</Badge>
                                        </HStack>
                                        <Heading size="md">{proposal.title}</Heading>
                                      </VStack>
                                      <HStack>
                                        <Text color="gray.400">Ended: {proposal.endDate}</Text>
                                      </HStack>
                                    </Flex>
                                  </CardHeader>
                                  <CardBody>
                                    <Text color="gray.400" mb={4}>{proposal.description}</Text>
                                    <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                      <Box>
                                        <Text fontSize="sm" color="gray.500">Total Votes</Text>
                                        <Text fontSize="lg" fontWeight="bold">
                                          {proposal.options.reduce((acc, opt) => acc + opt.votes, 0)}
                                        </Text>
                                      </Box>
                                      <Box>
                                        <Text fontSize="sm" color="gray.500">Final Result</Text>
                                        <Text fontSize="lg" fontWeight="bold" color={proposal.result === "Approved" ? "green.400" : "red.400"}>
                                          {proposal.result}
                                        </Text>
                                      </Box>
                                    </Grid>
                                  </CardBody>
                                </PirateCard>
                              ))}
                            </VStack>
                          ) : (
                            <Text color={grayTextColor}>No completed proposals.</Text>
                          )}
                        </TabPanel>
                      </TabPanels>
                    </Tabs>
                  </CardBody>
                </PirateCard>
                
                {/* Admin Controls (only visible to DAO admins) */}
                {publicKey && (
                  <PirateCard>
                    <CardHeader>
                      <Heading size="md">DAO Admin Controls</Heading>
                    </CardHeader>
                    <CardBody>
                      <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(3, 1fr)" }} gap={4}>
                        <Button colorScheme="blue" size="lg" height="100px" variant="outline">
                          Manage Revenue Distribution
                        </Button>
                        <Button colorScheme="purple" size="lg" height="100px" variant="outline">
                          Adjust Reserve Pool
                        </Button>
                        <Button colorScheme="orange" size="lg" height="100px" variant="outline">
                          Emergency Controls
                        </Button>
                      </Grid>
                    </CardBody>
                  </PirateCard>
                )}
                
                {/* Create Proposal Modal */}
                <Modal isOpen={isOpen} onClose={onClose}>
                  <ModalOverlay />
                  <ModalContent bg={darkBg} borderColor={goldColor} borderWidth="1px">
                    {selectedProposal && (
                      // Vote on Proposal Modal
                      <>
                        <ModalHeader color="white">Cast Your Vote</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                          <VStack spacing={4} align="stretch">
                            <Text fontWeight="bold" mb={2}>{selectedProposal.title}</Text>
                            <Text fontSize="sm" mb={4}>{selectedProposal.description}</Text>
                            
                            <VStack spacing={3} align="stretch">
                              {selectedProposal.options?.map((option) => (
                                <PirateButton
                                  key={option.id}
                                  onClick={() => handleVote(option.id)}
                                  variant="solid"
                                  size="lg"
                                  width="100%"
                                >
                                  <Flex justify="space-between" width="100%">
                                    <Text>{option.text}</Text>
                                    <Text>{option.percentage ?? 0}%</Text>
                                  </Flex>
                                </PirateButton>
                              ))}
                            </VStack>
                            
                            <Text fontSize="sm" mt={4} color={grayTextColor}>
                              Your voting power: {totalVotingPower} votes
                            </Text>
                          </VStack>
                        </ModalBody>
                        <ModalFooter>
                          <PirateButton variant="ghost" mr={3} onClick={onClose}>
                            Cancel
                          </PirateButton>
                        </ModalFooter>
                      </>
                    )}
                  </ModalContent>
                </Modal>
              </TabPanel>
              <TabPanel>
                {/* DAO Stats */}
                <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4} mb={8}>
                  <PirateCard>
                    <VStack p={4} align="center">
                      <Text color="whiteAlpha.700">Total Proposals</Text>
                      <Text fontSize="2xl" fontWeight="bold" color={goldColor}>{daoStats.totalProposals}</Text>
                    </VStack>
                  </PirateCard>
                  <PirateCard>
                    <VStack p={4} align="center">
                      <Text color="whiteAlpha.700">Active Proposals</Text>
                      <Text fontSize="2xl" fontWeight="bold" color={goldColor}>{daoStats.activeProposals}</Text>
                    </VStack>
                  </PirateCard>
                  <PirateCard>
                    <VStack p={4} align="center">
                      <Text color="whiteAlpha.700">Total Voters</Text>
                      <Text fontSize="2xl" fontWeight="bold" color={goldColor}>{daoStats.totalVoters}</Text>
                    </VStack>
                  </PirateCard>
                  <PirateCard>
                    <VStack p={4} align="center">
                      <Text color="whiteAlpha.700">Active Voters</Text>
                      <Text fontSize="2xl" fontWeight="bold" color={goldColor}>{daoStats.activeVoters}</Text>
                    </VStack>
                  </PirateCard>
                  <PirateCard>
                    <VStack p={4} align="center">
                      <Text color="whiteAlpha.700">Participation</Text>
                      <Text fontSize="2xl" fontWeight="bold" color={goldColor}>{daoStats.participationRate}</Text>
                    </VStack>
                  </PirateCard>
                  <PirateCard>
                    <VStack p={4} align="center">
                      <Text color="whiteAlpha.700">Pass Rate</Text>
                      <Text fontSize="2xl" fontWeight="bold" color={goldColor}>{daoStats.passRate}</Text>
                    </VStack>
                  </PirateCard>
                </SimpleGrid>

                {/* Proposals Section */}
                <Box mb={6}>
                  <Flex justify="space-between" align="center" mb={4}>
                    <Heading size="md">Proposals</Heading>
                    <ButtonGroup>
                      <PirateButton 
                        leftIcon={<AddIcon />} 
                        onClick={onOpen}
                        isDisabled={!publicKey}
                      >
                        Create Proposal
                      </PirateButton>
                      <IconButton
                        icon={<Icon as={GiPirateFlag} />}
                        aria-label="Filter"
                        variant="outline"
                        borderColor={goldColor}
                        _hover={{ bg: 'rgba(255, 215, 0, 0.1)' }}
                      />
                    </ButtonGroup>
                  </Flex>

                  <Tabs variant="line" colorScheme="yellow">
                    <TabList>
                      <Tab>Active</Tab>
                      <Tab>Completed</Tab>
                      <Tab>My Proposals</Tab>
                      <Tab>My Votes</Tab>
                    </TabList>

                    <TabPanels>
                      {/* Active Proposals */}
                      <TabPanel px={0}>
                        {proposals.filter(p => p.status === 'active').length > 0 ? (
                          <VStack spacing={4} align="stretch">
                            {proposals.filter(p => p.status === 'active').map(proposal => (
                              <PirateCard key={proposal.id} variant="outline" bg="gray.900">
                                <CardHeader pb={2}>
                                  <Flex justifyContent="space-between" alignItems="center">
                                    <Badge 
                                      colorScheme={getCategoryColor(proposal.category)}
                                      bg={
                                        proposal.category === 'Staking' 
                                          ? 'rgba(255, 255, 0, 0.2)' 
                                          : proposal.category === 'Technology'
                                          ? 'rgba(132, 204, 22, 0.2)'
                                          : undefined
                                      }
                                      color={
                                        proposal.category === 'Staking' 
                                          ? 'yellow.300' 
                                          : proposal.category === 'Technology'
                                          ? 'lime.300'
                                          : undefined
                                      }
                                    >
                                      {proposal.category}
                                    </Badge>
                                    <Badge 
                                      colorScheme="green" 
                                      variant="solid" 
                                      borderRadius="full"
                                      bg="rgba(34, 197, 94, 0.3)"
                                      color="green.300"
                                      px={3}
                                      py={1}
                                      fontSize="xs"
                                    >
                                      Active
                                    </Badge>
                                  </Flex>
                                </CardHeader>
                                <CardBody pt={2}>
                                  <Heading size="md" mb={3} color="white">{proposal.title}</Heading>
                                  <Text fontSize="sm" mb={3} color="gray.400" noOfLines={2}>{proposal.description}</Text>
                                  <HStack fontSize="xs" color="gray.500" mb={4} spacing={2}>
                                    <Text>Created by: {proposal.creator}</Text>
                                    <Text>•</Text>
                                    <Text>Ends: {proposal.endDate}</Text>
                                  </HStack>
                                  
                                  <Box mb={4} position="relative">
                                    {/* Progress Bar */}
                                    <Box
                                      w="100%"
                                      h="8px"
                                      position="relative"
                                      borderRadius="full"
                                      overflow="hidden"
                                    >
                                      {/* Red Background (Reject) */}
                                      <Box
                                        position="absolute"
                                        left="0"
                                        w={`${proposal.options[1].percentage}%`}
                                        h="100%"
                                        bg="red.300"
                                        opacity="0.5"
                                      />
                                      {/* Green Background (Approve) */}
                                      <Box
                                        position="absolute"
                                        right="0"
                                        w={`${proposal.options[0].percentage}%`}
                                        h="100%"
                                        bg="green.300"
                                        opacity="0.5"
                                      />
                                    </Box>

                                    {/* Vote Information */}
                                    <Flex justify="space-between" mt={2}>
                                      <Box>
                                        <Text fontSize="sm" color="red.300" fontWeight="bold">
                                          Reject ({proposal.options[1].percentage}%)
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                          {proposal.options[1].votes} votes
                                        </Text>
                                      </Box>
                                      <Box textAlign="right">
                                        <Text fontSize="sm" color="green.300" fontWeight="bold">
                                          Approve ({proposal.options[0].percentage}%)
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                          {proposal.options[0].votes} votes
                                        </Text>
                                      </Box>
                                    </Flex>
                                  </Box>

                                  {hasVoted(proposal.id) ? (
                                    <Box 
                                      p={4} 
                                      textAlign="center" 
                                      bg="rgba(52, 211, 153, 0.1)"
                                      borderRadius="lg"
                                      border="1px solid"
                                      borderColor={getUserVote(proposal.id) === 'approve' ? 'green.300' : 'red.300'}
                                    >
                                      <Text 
                                        color={getUserVote(proposal.id) === 'approve' ? 'green.300' : 'red.300'}
                                        fontWeight="medium"
                                      >
                                        You voted to {getUserVote(proposal.id)} this proposal
                                      </Text>
                                    </Box>
                                  ) : (
                                    <HStack spacing={4} justify="center">
                                      <Button
                                        leftIcon={<CloseIcon />}
                                        bg="transparent"
                                        color="red.300"
                                        flex={1}
                                        maxW="200px"
                                        fontSize="sm"
                                        h="40px"
                                        border="1px solid"
                                        borderColor="red.300"
                                        borderRadius="full"
                                        boxShadow="0 0 10px rgba(254, 78, 78, 0.2)"
                                        transition="all 0.2s"
                                        onClick={() => handleVote(proposal.id, 'reject')}
                                        _hover={{
                                          bg: "rgba(254, 78, 78, 0.1)",
                                          boxShadow: "0 0 15px rgba(254, 78, 78, 0.3)"
                                        }}
                                        _active={{
                                          bg: "rgba(254, 78, 78, 0.15)"
                                        }}
                                      >
                                        Reject
                                      </Button>
                                      <Button
                                        leftIcon={<CheckIcon />}
                                        bg="transparent"
                                        color="green.300"
                                        flex={1}
                                        maxW="200px"
                                        fontSize="sm"
                                        h="40px"
                                        border="1px solid"
                                        borderColor="green.300"
                                        borderRadius="full"
                                        boxShadow="0 0 10px rgba(52, 211, 153, 0.2)"
                                        transition="all 0.2s"
                                        onClick={() => handleVote(proposal.id, 'approve')}
                                        _hover={{
                                          bg: "rgba(52, 211, 153, 0.1)",
                                          boxShadow: "0 0 15px rgba(52, 211, 153, 0.3)"
                                        }}
                                        _active={{
                                          bg: "rgba(52, 211, 153, 0.15)"
                                        }}
                                      >
                                        Approve
                                      </Button>
                                    </HStack>
                                  )}
                                </CardBody>
                              </PirateCard>
                            ))}
                          </VStack>
                        ) : (
                          <Text color={grayTextColor}>No active proposals at the moment.</Text>
                        )}
                      </TabPanel>
                      
                      {/* Completed Proposals */}
                      <TabPanel px={0}>
                        {completedProps.length > 0 ? (
                          <VStack spacing={4} align="stretch">
                            {completedProps.map(proposal => (
                              <PirateCard key={proposal.id} variant="outline" bg="gray.900">
                                <CardHeader pb={2}>
                                  <Flex justifyContent="space-between" alignItems="center">
                                    <VStack align="start" spacing={1}>
                                      <HStack>
                                        <Badge colorScheme={proposal.result === "Approved" ? "green" : "red"}>
                                          {proposal.result}
                                        </Badge>
                                        <Badge colorScheme="purple">{proposal.category}</Badge>
                                      </HStack>
                                      <Heading size="md">{proposal.title}</Heading>
                                    </VStack>
                                    <HStack>
                                      <Text color="gray.400">Ended: {proposal.endDate}</Text>
                                    </HStack>
                                  </Flex>
                                </CardHeader>
                                <CardBody>
                                  <Text color="gray.400" mb={4}>{proposal.description}</Text>
                                  <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                    <Box>
                                      <Text fontSize="sm" color="gray.500">Total Votes</Text>
                                      <Text fontSize="lg" fontWeight="bold">
                                        {proposal.options.reduce((acc, opt) => acc + opt.votes, 0)}
                                      </Text>
                                    </Box>
                                    <Box>
                                      <Text fontSize="sm" color="gray.500">Final Result</Text>
                                      <Text fontSize="lg" fontWeight="bold" color={proposal.result === "Approved" ? "green.400" : "red.400"}>
                                        {proposal.result}
                                      </Text>
                                    </Box>
                                  </Grid>
                                </CardBody>
                              </PirateCard>
                            ))}
                          </VStack>
                        ) : (
                          <Text color={grayTextColor}>No completed proposals.</Text>
                        )}
                      </TabPanel>

                      {/* My Proposals Tab */}
                      <TabPanel px={0}>
                        <Box textAlign="center" py={6}>
                          <Text mb={4}>You haven't created any proposals yet.</Text>
                          <PirateButton 
                            leftIcon={<AddIcon />} 
                            onClick={onOpen}
                            isDisabled={!publicKey}
                          >
                            Create Your First Proposal
                          </PirateButton>
                        </Box>
                      </TabPanel>

                      {/* My Votes Tab */}
                      <TabPanel px={0}>
                        <Box textAlign="center" py={6}>
                          <Text mb={4}>You haven't voted on any proposals yet.</Text>
                          <Text fontSize="sm" color="gray.500">
                            Start participating in the DAO by voting on active proposals!
                          </Text>
                        </Box>
                      </TabPanel>
                    </TabPanels>
                  </Tabs>
                </Box>
              </TabPanel>
              <TabPanel>
                {/* Treasury */}
                {renderTreasuryTab()}
              </TabPanel>
              <TabPanel>
                {/* My NFTs */}
                {renderMyNFTsTab()}
              </TabPanel>
              <TabPanel>
                {/* Revenue */}
                <VStack spacing={8} align="stretch">
                  {/* Overview Cards */}
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                    <PirateCard>
                      <CardHeader>
                        <Heading size="md">Total Earned</Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack align="stretch" spacing={2}>
                          <Stat>
                            <StatLabel>JINU</StatLabel>
                            <StatNumber color="#32CD32">
                              {revenueData.totalEarned?.jinu?.toLocaleString(undefined, { maximumFractionDigits: 2 })} JINU
                            </StatNumber>
                            <StatHelpText color="#32CD32">
                              <StatArrow type="increase" />
                              23.36%
                            </StatHelpText>
                            <StatHelpText color="#32CD32">
                              ${(revenueData.totalEarned?.usd || 0).toFixed(2)} USD
                            </StatHelpText>
                          </Stat>
                          <Divider />
                          <Stat>
                            <StatLabel>USD</StatLabel>
                            <StatNumber color="#32CD32">
                              ${(revenueData.totalEarned?.usd || 0).toFixed(2)} USD
                            </StatNumber>
                            <StatHelpText color="#32CD32">
                              <StatArrow type="increase" />
                              23.36%
                            </StatHelpText>
                          </Stat>
                          <Divider />
                          <Stat>
                            <StatLabel>JINU</StatLabel>
                            <StatNumber color="#32CD32">
                              {revenueData.totalEarned?.jinu?.toLocaleString(undefined, { maximumFractionDigits: 2 })} JINU
                            </StatNumber>
                            <StatHelpText color="#32CD32">
                              <StatArrow type="increase" />
                              23.36%
                            </StatHelpText>
                            <StatHelpText color="#32CD32">
                              ${(revenueData.totalEarned?.usd || 0).toFixed(2)} USD
                            </StatHelpText>
                          </Stat>
                        </VStack>
                      </CardBody>
                    </PirateCard>

                    <PirateCard>
                      <CardHeader>
                        <Heading size="md">Pending Rewards</Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack align="stretch" spacing={2}>
                          <Stat>
                            <StatLabel>JINU</StatLabel>
                            <StatNumber color="#32CD32">
                              {revenueData.pendingRewards?.jinu?.toLocaleString(undefined, { maximumFractionDigits: 2 })} JINU
                            </StatNumber>
                            <StatHelpText color="#32CD32">
                              <StatArrow type="increase" />
                              12.45%
                            </StatHelpText>
                            <StatHelpText color="#32CD32">
                              ${(revenueData.pendingRewards?.usd || 0).toFixed(2)} USD
                            </StatHelpText>
                          </Stat>
                          <Divider />
                          <Stat>
                            <StatLabel>USD</StatLabel>
                            <StatNumber color="#32CD32">
                              ${(revenueData.pendingRewards?.usd || 0).toFixed(2)} USD
                            </StatNumber>
                            <StatHelpText color="#32CD32">
                              <StatArrow type="increase" />
                              12.45%
                            </StatHelpText>
                          </Stat>
                          <Divider />
                          <Stat>
                            <StatLabel>JINU</StatLabel>
                            <StatNumber color="#32CD32">
                              {revenueData.pendingRewards?.jinu?.toLocaleString(undefined, { maximumFractionDigits: 2 })} JINU
                            </StatNumber>
                            <StatHelpText color="#32CD32">
                              <StatArrow type="increase" />
                              12.45%
                            </StatHelpText>
                            <StatHelpText color="#32CD32">
                              ${(revenueData.pendingRewards?.usd || 0).toFixed(2)} USD
                            </StatHelpText>
                          </Stat>
                        </VStack>
                      </CardBody>
                    </PirateCard>

                    <PirateCard>
                      <CardHeader>
                        <Heading size="md">Last Claim</Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack spacing={4}>
                          <Text fontSize="xl">{revenueData.lastClaim}</Text>
                          <PirateButton 
                            leftIcon={<Icon as={GiCoins} />}
                            isDisabled={revenueData.pendingRewards?.jinu === 0}
                            onClick={() => {
                              toast({
                                title: "Rewards Claimed",
                                description: "Your rewards have been successfully claimed!",
                                status: "success",
                                duration: 5000,
                                isClosable: true,
                              });
                            }}
                          >
                            Claim Rewards
                          </PirateButton>
                        </VStack>
                      </CardBody>
                    </PirateCard>
                  </SimpleGrid>

                  {/* Revenue Streams */}
                  <PirateCard>
                    <CardHeader>
                      <Heading size="md">Revenue Streams</Heading>
                    </CardHeader>
                    <CardBody>
                      <VStack spacing={6} align="stretch">
                        {revenueData.revenueStreams?.map((stream, index) => (
                          <PirateCard key={index} variant="outline">
                            <CardBody>
                              <Flex justify="space-between" align="center">
                                <Box>
                                  <Text fontWeight="bold">{stream.name}</Text>
                                  <Text fontSize="sm" color="gray.500">
                                    {stream.amount?.toFixed(2) ?? 0} JINU
                                  </Text>
                                  <Text fontSize="sm" color="gray.500">
                                    ${(stream.amount * 0.0136).toFixed(2)} USD
                                  </Text>
                                </Box>
                                <HStack spacing={4}>
                                  <Text color={stream.trend === 'up' ? 'green.500' : 'red.500'}>
                                    {stream.percentage?.toFixed(1) ?? 0}%
                                  </Text>
                                  <Icon 
                                    as={stream.trend === 'up' ? FiTrendingUp : FiTrendingDown} 
                                    color={stream.trend === 'up' ? 'green.500' : 'red.500'} 
                                  />
                                </HStack>
                              </Flex>
                            </CardBody>
                          </PirateCard>
                        ))}
                      </VStack>
                    </CardBody>
                  </PirateCard>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Flex>
      </Box>
      
      {/* Create Proposal Button */}
      {!publicKey && (
        <Box position="fixed" bottom="20px" right="20px" zIndex="1">
          <PirateButton 
            leftIcon={<AddIcon />} 
            onClick={onOpen}
            isDisabled={true}
          >
            Create Proposal
          </PirateButton>
        </Box>
      )}
    </Box>
  );
};

// Add back the required functions and components
const getAdjustmentPresets = (poolName, adjustmentType) => {
  const maxLimit = POOL_LIMITS[poolName];
  if (!maxLimit) return [1, 2, 5];

  if (maxLimit <= 5) {
    return adjustmentType === 'increase' 
      ? [1, 2, maxLimit]
      : [1, 2, 3];
  } else if (maxLimit <= 20) {
    return adjustmentType === 'increase'
      ? [5, 10, maxLimit]
      : [5, 10, 15];
  } else if (maxLimit <= 50) {
    return adjustmentType === 'increase'
      ? [10, 25, maxLimit]
      : [10, 25, 35];
  } else {
    return adjustmentType === 'increase'
      ? [20, 35, maxLimit]
      : [20, 35, 50];
  }
};

const calculateTotalStats = () => {
  const TOTAL_NFTS = 777;
  const mockNFTs = Array.from({ length: TOTAL_NFTS }, (_, i) => ({
    id: i + 1,
    totalEarned: getRandomDailyEarning() * 365,
    lastReward: getRandomDailyEarning()
  }));

  const stats = {
    totalNFTs: TOTAL_NFTS,
    stakedNFTs: Math.floor(TOTAL_NFTS * 0.85), // Assume 85% staked
    totalEarned: mockNFTs.reduce((sum, nft) => sum + nft.totalEarned, 0),
    lastReward: mockNFTs.reduce((sum, nft) => sum + nft.lastReward, 0),
    averagePerNFT: getRandomDailyEarning() // Daily average per NFT
  };

  return stats;
};

const getRandomDailyEarning = () => {
  const min = 900;
  const max = 1300;
  const value = min + (Math.random() * (max - min));
  return Number(value.toFixed(2));
};

const RevenueDisplay = ({ data = {
  totalEarned: { 
    jinu: 1500.25,
    usd: 150025.00
  },
  pendingRewards: {
    jinu: 250.75,
    usd: 25075.00
  },
  lastClaim: "2025-03-30",
  revenueHistory: [
    { date: "2025-03", amount: 500.75 },
    { date: "2025-02", amount: 450.50 },
    { date: "2025-01", amount: 549.00 }
  ]
} }) => {
  return (
    <VStack spacing={4} align="stretch">
      <Stat>
        <StatLabel color="gray.400">Total Revenue Generated</StatLabel>
        <StatNumber color="#32CD32">
          {data.totalEarned?.jinu?.toLocaleString(undefined, { maximumFractionDigits: 2 })} JINU
        </StatNumber>
        <StatHelpText color="#32CD32">
          Last distribution: {data.lastClaim || "No claims yet"}
        </StatHelpText>
        <StatHelpText color="#32CD32">
          ${(data.totalEarned?.usd || 0).toFixed(2)} USD
        </StatHelpText>
      </Stat>
    </VStack>
  );
};

export default DAODashboard;
